import client from './client';
import { _createAlert } from './apiUtils';
import type {
    Project,
    NearestProjectsResponse,
    ProjectMapPin,
    ProjectMapSupervisorsResponse,
    ProjectMapCrewResponse,
} from '../interfaces/Project';
import type { ProjectTasksResponse } from '../interfaces/ProjectTasksInterface';

/** Session-level flag — true once the typeahead endpoint returned 404 (not yet deployed). */
let _typeaheadFailed = false;

export default class ProjectsAPI {
    private static getDownloadFilename(contentDisposition?: string, fallback?: string) {
        if (!contentDisposition) return fallback;

        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (filenameStarMatch?.[1]) {
            return decodeURIComponent(filenameStarMatch[1].trim().replace(/^"|"$/g, ""));
        }

        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (filenameMatch?.[1]) {
            return filenameMatch[1].trim();
        }

        return fallback;
    }

    static async search(searchString: string) {
        try {

            // Make the API request to fetch the employees' data based on the constructed endpoint and parameters
            const response = await client.get("admin/projects/search", {
                params: {
                    searchValue: searchString,  // Pass the search values
                }
            });

            // Log the response data to the console
            console.log(response.data);

            // Return the response object, which contains the employees' data
            return response;

        } catch (error: any) {
            // Log any errors that occur during the upload process
            console.error("Error uploading file:", error);
            // Trigger an alert with the error message
            _createAlert("Upload failed:", `${error.message}`);
            // Throw an error to indicate that the upload failed
            throw new Error("Failed to upload file.");
        }
    }

    static async searchAll(searchString: string) {
        try {

            // Make the API request to fetch all projects (including inactive) based on the constructed endpoint and parameters
            const response = await client.get("admin/projects/searchAll", {
                params: {
                    searchValue: searchString,  // Pass the search values
                }
            });

            // Log the response data to the console
            console.log(response.data);

            // Return the response object, which contains the projects' data
            return response;

        } catch (error: any) {
            // Log any errors that occur during the upload process
            console.error("Error searching projects:", error);
            // Trigger an alert with the error message
            _createAlert("Search failed:", `${error.message}`);
            // Throw an error to indicate that the search failed
            throw new Error("Failed to search projects.");
        }
    }

    static async updateProjectGeolocation(
        projectID: number,
        body: { latitude: number; longitude: number; radiusMiles: number }
    ) {
        try {
            const response = await client.put<Project>(`admin/projects/${projectID}/geolocation`, body);
            return response;
        } catch (error: any) {
            console.error("Error updating project geolocation:", error);
            throw error;
        }
    }

    static async getCompleteProject(projectID: number) {
        try {
            // Make the API request to fetch the complete project structure
            const response = await client.get(`admin/projects/${projectID}/complete`);

            // Log the response data to the console
            console.log(response.data);

            // Return the response object, which contains the complete project data
            return response;

        } catch (error: any) {
            // Log any errors that occur during the fetch process
            console.error("Error fetching complete project:", error);
            // Trigger an alert with the error message
            _createAlert("Fetch failed:", `${error.message}`);
            // Throw an error to indicate that the fetch failed
            throw new Error("Failed to fetch complete project.");
        }
    }

    /**
     * Every task on a project — direct and intermediate-linked — flat with its
     * breadcrumb, plus the intermediate tree, in one call. Feeds the payroll
     * "Add task claim" picker (the lightweight replacement for `/complete`).
     *
     * Deliberately has NO `_createAlert`: the picker owns its own error state
     * (a designed in-modal retry that keeps the project selected), so a global
     * alert on top of it would be a second, worse copy of the same message.
     */
    static async getProjectTasks(projectID: number): Promise<ProjectTasksResponse> {
        try {
            // Make the API request to fetch every task on the project
            const response = await client.get<ProjectTasksResponse>(`admin/projects/${projectID}/tasks`);

            // Return the parsed payload — tasks, intermediates and counts
            return response.data;

        } catch (error: any) {
            // Log any errors that occur during the fetch process; the caller shows the retry
            console.error("Error fetching project tasks:", error);
            // Re-throw so the picker can render its failed state
            throw error;
        }
    }

    static async projectAddUpdatePieces(altProjectID: string, companyID: number) {
        try {
            const response = await client.post<{
                success: boolean;
                message: string;
                updates?: { piecesAdded: number; piecesUpdated: number; piecesRemoved: number };
            }>(
                "/admin/riceServices/projectAddUpdatePieces",
                { altProjectID, companyID }
            );
            return response;
        } catch (error: any) {
            console.error("Error syncing pieces:", error);
            _createAlert("Sync failed:", `${error.message}`);
            throw new Error("Failed to sync pieces.");
        }
    }

    static async refreshPieceCache(): Promise<{ status: string; jobID: number }> {
        try {
            const response = await client.post<{ status: string; jobID: number }>(
                "/admin/riceServices/refreshPieceCache",
                {}
            );
            if (!response.data || response.data.jobID == null) {
                throw new Error("No jobID in response");
            }
            return response.data;
        } catch (error: any) {
            console.error("Error refreshing piece cache:", error);
            _createAlert("Refresh failed:", `${error.message}`);
            throw new Error("Failed to refresh piece cache.");
        }
    }

    static async downloadPieceSheet(projectID: number): Promise<{ blob: Blob; fileName: string }> {
        try {
            const response = await client.get(`/admin/wcdp/piece-sheet/${projectID}`, {
                responseType: "blob"
            });

            const fallbackName = `PieceSheet_${projectID}.xlsx`;
            const contentDisposition = response.headers?.["content-disposition"] as string | undefined;
            const fileName = this.getDownloadFilename(contentDisposition, fallbackName) ?? fallbackName;

            return { blob: response.data as Blob, fileName };
        } catch (error: any) {
            console.error("Error downloading piece sheet:", error);
            _createAlert("Download failed:", `${error.message}`);
            throw error;
        }
    }

    static async downloadMiscTasks(projectID: number): Promise<{ blob: Blob; fileName: string }> {
        try {
            const response = await client.get(`admin/projects/${projectID}/miscTasksExcel`, {
                responseType: "blob"
            });

            const fallbackName = `MiscTasks_${projectID}.xlsx`;
            const contentDisposition = response.headers?.["content-disposition"] as string | undefined;
            const fileName = this.getDownloadFilename(contentDisposition, fallbackName) ?? fallbackName;

            return { blob: response.data as Blob, fileName };
        } catch (error: any) {
            console.error("Error downloading misc tasks export:", error);
            _createAlert("Download failed:", `${error.message}`);
            throw error;
        }
    }

    /**
     * Typeahead search: GET admin/projects/typeahead?q=<q>&limit=<limit>.
     * Returns Project[] (same mapped shape as searchAll).
     * Falls back transparently to searchAll on 404 and remembers the fallback for the session.
     */
    static async typeahead(q: string, limit = 25): Promise<Project[]> {
        if (!_typeaheadFailed) {
            try {
                const response = await client.get<Project[]>('admin/projects/typeahead', {
                    params: { q, limit },
                });
                return response.data ?? [];
            } catch (error: any) {
                if (error?.response?.status === 404) {
                    // Endpoint not yet deployed — fall back to searchAll for this session.
                    _typeaheadFailed = true;
                } else {
                    throw error;
                }
            }
        }
        // Fallback: reuse the existing searchAll endpoint.
        const response = await client.get<Project[]>('admin/projects/searchAll', {
            params: { searchValue: q },
        });
        return response.data ?? [];
    }

    static async getMapPins(): Promise<ProjectMapPin[]> {
        const response = await client.get<ProjectMapPin[]>("/admin/projects/mapPins");
        return response.data;
    }

    /** The nearest active project to each point (Day Punch Map). Throws on failure. */
    static async getNearestProjects(points: Array<{ lat: number; lng: number }>): Promise<NearestProjectsResponse> {
        const response = await client.post<NearestProjectsResponse>("/admin/projects/nearest", { points });
        return response.data;
    }

    /** Supervisors for the Project Map "Super" filter / popup names (sorted last, first). */
    static async getMapSupervisors(): Promise<ProjectMapSupervisorsResponse> {
        const response = await client.get<ProjectMapSupervisorsResponse>("/admin/projects/mapSupervisors");
        return response.data;
    }

    /**
     * Currently-clocked-in employees for the Project Map Live Crew layer.
     * Throws the raw axios error so callers can detect 403 (no crew access)
     * and silently hide the crew UI.
     */
    static async getMapCrew(): Promise<ProjectMapCrewResponse> {
        const response = await client.get<ProjectMapCrewResponse>("/admin/projects/mapCrew");
        return response.data;
    }
}